"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _Autocomplete = _interopRequireDefault(require("@mui/material/Autocomplete"));
var _Box = _interopRequireDefault(require("@mui/material/Box"));
var _Grid = _interopRequireDefault(require("@mui/material/Grid2"));
var _TextField = _interopRequireDefault(require("@mui/material/TextField"));
var _styles = require("@mui/styles");
var _match = _interopRequireDefault(require("autosuggest-highlight/match"));
var _parse = _interopRequireDefault(require("autosuggest-highlight/parse"));
var _throttle = _interopRequireDefault(require("lodash/throttle"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _Flex = _interopRequireDefault(require("./Flex"));
var _String = _interopRequireDefault(require("./String"));
var _LocationOn = _interopRequireDefault(require("@mui/icons-material/LocationOn"));
var _Search = _interopRequireDefault(require("@mui/icons-material/Search"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const useStyles = (0, _styles.makeStyles)(theme => ({
  container: {
    borderColor: theme.palette.grey[300],
    borderRadius: theme.spacing(0.5),
    borderStyle: "solid",
    borderWidth: "1px",
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1)
  },
  iconLocation: {
    color: theme.palette.grey[700],
    marginRight: theme.spacing(0.5)
  },
  iconSearch: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(0.5)
  },
  textarea: {
    backgroundColor: theme.palette.grey[50]
  }
}));
const AddressLookup = props => {
  const {
    getAddressClient,
    NoOptionsText,
    onAddressSelected
  } = props;
  const classes = useStyles();
  const [value, setValue] = (0, _react.useState)(null);
  const [inputValue, setInputValue] = (0, _react.useState)("");
  const [options, setOptions] = (0, _react.useState)([]);

  /**
   * Fetch Autocomplete results from the getAddress.io service
   */
  const fetch = (0, _react.useMemo)(() => (0, _throttle.default)(async (request, callback) => {
    const autocompleteResult = await getAddressClient.autocomplete(request.input);
    if (autocompleteResult.isSuccess) {
      const success = autocompleteResult.toSuccess();
      callback(success.suggestions);
    } else {
      // const failed = autocompleteResult.toFailed();
      callback([]);
    }
  }, 200), [getAddressClient]);

  /**
   * Hook to fetch new Autocomplete suggestions as the input value changes
   */
  (0, _react.useEffect)(() => {
    let active = true;
    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }
    fetch({
      input: inputValue
    }, results => {
      if (active) {
        let newOptions = [];
        if (value) {
          newOptions = [value];
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });
    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);
  const handleChange = async (e, value) => {
    setOptions(value ? [value, ...options] : options);
    setValue(value);

    // Lookup full address from autocomplete suggestion ID and pass back to parent
    if (value?.id) {
      const addressResult = await getAddressClient.get(value.id);
      if (addressResult.isSuccess) {
        const success = addressResult.toSuccess();

        // Map to a standard HOPS Address object
        const addressLine = [success.address.line_1, success.address.line_2, success.address.line_3, success.address.line_4].filter(x => x).join(", ");
        const cityLine = [success.address.locality, success.address.town_or_city].filter(x => x).join(", ");
        const newAddress = {
          Address: addressLine,
          City: cityLine,
          County: success.address.county,
          Country: "GB",
          Postcode: success.address.postcode
        };
        onAddressSelected?.(newAddress);
        setValue(null);
      } else {
        // callback([]);
      }
    }
  };
  const handleInputChange = (e, value) => {
    setInputValue(value);
  };

  /**
   * Render an input box for the user to start typing their address
   */
  const renderInput = params => {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
      gap: 1,
      className: classes.container,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
        gap: 0,
        columnar: true,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Search.default, {
          className: classes.iconSearch
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
          bold: true,
          color: "primary",
          str: "Address Lookup"
        })]
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_TextField.default, {
        className: classes.textarea,
        ...params,
        label: NoOptionsText,
        variant: "outlined",
        size: "small",
        fullWidth: true
      })]
    });
  };

  /**
   * Render an autocomplete suggestion from the results
   */
  const renderOption = (props, option) => {
    const {
      key,
      ...optionProps
    } = props;
    const matches = (0, _match.default)(option.address, inputValue);
    const parts = (0, _parse.default)(option.address, matches);
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("li", {
      ...optionProps,
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Grid.default, {
        container: true,
        sx: {
          alignItems: "center"
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Grid.default, {
          sx: {
            display: "flex",
            width: 44
          },
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_LocationOn.default, {
            sx: {
              color: "text.secondary"
            }
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Grid.default, {
          sx: {
            width: "calc(100% - 44px)",
            wordWrap: "break-word"
          },
          children: parts.map((part, index) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_Box.default, {
            component: "span",
            sx: {
              fontWeight: part.highlight ? "fontWeightBold" : "fontWeightRegular"
            },
            children: part.text
          }, index))
        })]
      })
    }, key);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Autocomplete.default, {
    style: {
      width: "100%"
    },
    getOptionLabel: option => typeof option === "string" ? option : option.address,
    filterOptions: x => x,
    forcePopupIcon: true,
    options: options,
    autoComplete: true,
    noOptionsText: NoOptionsText,
    includeInputInList: true,
    filterSelectedOptions: true,
    value: value,
    onChange: handleChange,
    onInputChange: handleInputChange,
    renderInput: renderInput,
    renderOption: renderOption
  });
};
AddressLookup.propTypes = {
  getAddressClient: _propTypes.default.object,
  NoOptionsText: _propTypes.default.string,
  TextFieldVariant: _propTypes.default.oneOf(["outlined", "standard"])
};
AddressLookup.defaultProps = {
  NoOptionsText: "Enter the postcode or the first line of the address",
  TextFieldVariant: "outlined"
};
var _default = exports.default = AddressLookup;